import type {
  BoxDashboardKpiEntity,
  VariationDirection,
} from "@/modules/boxDashboard/modules/KPI/entities/boxDashboardKpiEntity"
import type { Language } from "@/modules/language/utils/languageUtils"
import type { Dayjs } from "dayjs"

export type KpiVariation = {
  percent: number | null
  direction: VariationDirection | null
}

export type IncomeBarModel = {
  focusAmount: number
  focusMonthLabel: string
  focusProgressPercent: number
  comparisonAmount: number | null
  comparisonMonthLabel: string | null
  comparisonProgressPercent: number
  variationPercent: number | null
  variationDirection: VariationDirection | null
  showComparisonBar: boolean
  hasComparison: boolean
}

export type MrrModel = {
  amount: number
  variationPercent: number | null
  variationDirection: VariationDirection | null
}

export type ShopSalesModel = {
  amount: number
  variationPercent: number | null
  variationDirection: VariationDirection | null
}

export type IncomeKpiModel = {
  incomeBar: IncomeBarModel
  mrr: MrrModel
  shopSales: ShopSalesModel
}

function calculateVariationPercent(
  focusAmount: number,
  comparisonAmount: number | null,
): { percent: number | null; direction: VariationDirection | null } {
  if (comparisonAmount === null || comparisonAmount === 0) {
    return { percent: null, direction: null }
  }

  const percent = Math.round(((focusAmount - comparisonAmount) / comparisonAmount) * 100)

  let direction: VariationDirection = "neutral"
  if (percent > 0) {
    direction = "up"
  } else if (percent < 0) {
    direction = "down"
  }

  return { percent, direction }
}

function calculateProgressPercents(
  focusAmount: number,
  comparisonAmount: number | null,
): { focusPercent: number; comparisonPercent: number } {
  if (comparisonAmount === null) {
    return { focusPercent: focusAmount === 0 ? 0 : 100, comparisonPercent: 0 }
  }

  const maxAmount = Math.max(focusAmount, comparisonAmount)

  if (maxAmount === 0) {
    return { focusPercent: 0, comparisonPercent: 0 }
  }

  return {
    focusPercent: (focusAmount / maxAmount) * 100,
    comparisonPercent: (comparisonAmount / maxAmount) * 100,
  }
}

export function createEmptyIncomeKpiModel(focusMonth: Dayjs, locale: Language): IncomeKpiModel {
  return {
    incomeBar: {
      focusAmount: 0,
      focusMonthLabel: focusMonth.locale(locale).format("MMMM YYYY"),
      focusProgressPercent: 0,
      comparisonAmount: null,
      comparisonMonthLabel: null,
      comparisonProgressPercent: 0,
      variationPercent: null,
      variationDirection: null,
      showComparisonBar: false,
      hasComparison: false,
    },
    mrr: {
      amount: 0,
      variationPercent: null,
      variationDirection: null,
    },
    shopSales: {
      amount: 0,
      variationPercent: null,
      variationDirection: null,
    },
  }
}

export function createIncomeKpiModel(
  entity: BoxDashboardKpiEntity,
  focusMonth: Dayjs,
  comparisonMonth: Dayjs | null,
  locale: Language,
): IncomeKpiModel {
  if (!entity.income) {
    return createEmptyIncomeKpiModel(focusMonth, locale)
  }
  const focusAmount = entity.income.total.baseline
  const comparisonAmount = entity.income.total.comparison

  const incomeVariation = calculateVariationPercent(focusAmount, comparisonAmount)
  const { focusPercent, comparisonPercent } = calculateProgressPercents(focusAmount, comparisonAmount)
  const hideComparison = comparisonAmount === null || (comparisonAmount === 0 && focusAmount === 0)

  const mrrVariation = calculateVariationPercent(entity.income.mrr.baseline, entity.income.mrr.comparison)
  const shopSalesVariation = calculateVariationPercent(
    entity.income.shopSales.baseline,
    entity.income.shopSales.comparison,
  )

  return {
    incomeBar: {
      focusAmount,
      focusMonthLabel: focusMonth.locale(locale).format("MMMM YYYY"),
      focusProgressPercent: focusPercent,
      comparisonAmount,
      comparisonMonthLabel: comparisonMonth ? comparisonMonth.locale(locale).format("MMMM YYYY") : null,
      comparisonProgressPercent: comparisonPercent,
      variationPercent: incomeVariation.percent,
      variationDirection: incomeVariation.direction,
      showComparisonBar: !hideComparison,
      hasComparison: !hideComparison,
    },
    mrr: {
      amount: entity.income.mrr.baseline,
      variationPercent: mrrVariation.percent,
      variationDirection: mrrVariation.direction,
    },
    shopSales: {
      amount: entity.income.shopSales.baseline,
      variationPercent: shopSalesVariation.percent,
      variationDirection: shopSalesVariation.direction,
    },
  }
}
